import { DateField, NumberField, TextField } from '@refinedev/antd';
import { useOne } from '@refinedev/core';
import { Alert, Flex, Form, InputNumber } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { ReceivedProductStatusStrings } from '@/model/Logistic/PackageItem/ReceivedProductStatus';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { ReturnItemTypeString } from '@/model/Logistic/Reception/ReturnItemType';

import usePackageItemForm from '../PackageItemForm/usePackageItemForm';

interface ReceptionPackageItemShowProps {
  formProps: FormProps;
  editingPackageItem: PackageItem;
  editEnabled: boolean;
  canEditResolution?: boolean;
}

const ReceptionPackageItemShow = (props: ReceptionPackageItemShowProps) => {
  const { t } = useTranslation();
  const { formProps, editingPackageItem, editEnabled, canEditResolution } = props;
  const {
    in_warranty_period,
    is_returned,
    return_type,
    item_received_state,
    warranty_start_date,
    item_shipment_related,
    package: itemPackage,
    product,
    unidad,
    device,
    product_type,
  } = editingPackageItem;
  // Cantidades
  const {
    requested_amount,
    execute_amount,
    technician_stock_amount,
    discarded_amount,
    used_amount,
    to_return_amount,
  } = editingPackageItem;
  const { productType, serialNumbersSelectProps } = usePackageItemForm(
    formProps,
    editingPackageItem,
    editEnabled,
  );
  const { result: productResult } = useOne({
    resource: 'elements',
    id: product,
  });
  const { result: serializableProductResult } = useOne({
    resource: 'elements',
    id: unidad ?? device,
    queryOptions: {
      enabled:
        (productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE) &&
        !editEnabled &&
        (!!unidad || !!device),
    },
    meta: { serializable: true, type: product_type },
  });
  const elementFormName = useMemo(() => {
    if (!editEnabled) return undefined;
    if (productType === PRODUCT_TYPES.UNIT) return 'unidad';
    if (productType === PRODUCT_TYPES.DEVICE) return 'device';
  }, [productType, editEnabled]);
  const elementFormPlaceholder = useMemo(() => {
    if (productType === PRODUCT_TYPES.PRODUCT) return 'Seleccione un producto';
    if (productType === PRODUCT_TYPES.UNIT) return 'Seleccione una unidad';
    if (productType === PRODUCT_TYPES.DEVICE) return 'Seleccione un dispositivo';

    return 'Seleccione un tipo de contenido';
  }, [productType]);
  // Estado del paquete
  const packageStatus = useMemo(
    () =>
      editingPackageItem && typeof editingPackageItem.package != 'number'
        ? editingPackageItem.package?.status
        : undefined,
    [editingPackageItem],
  );
  const hasTechnician = useMemo(
    () =>
      editingPackageItem && typeof editingPackageItem.package != 'number'
        ? !!editingPackageItem.package?.technician
        : false,
    [editingPackageItem],
  );
  const editableRelatedQuantities = useMemo(
    () => editEnabled && packageStatus == PACKAGE_STATES.PENDIENTE,
    [editEnabled, packageStatus],
  );
  // Form fields values
  const discarded_amount_field = Form.useWatch('discarded_amount', formProps.form);
  const to_return_amount_field = Form.useWatch('to_return_amount', formProps.form);
  const technician_stock_amount_field = Form.useWatch('technician_stock_amount', formProps.form);
  const isValidAmount =
    discarded_amount_field + to_return_amount_field + technician_stock_amount_field ==
    requested_amount;

  return (
    <>
      <fieldset>
        <legend>{t('Información del producto')}</legend>
        <Form.Item
          label={t('Tipo de contenido')}
          name={'product_type'}
          getValueProps={v => ({ value: t(ProductTypeStrings[v]) })}>
          <TextField value={t(ProductTypeStrings[product_type])} />
        </Form.Item>

        <Form.Item label={t('Producto')}>
          <TextField value={t(productResult?.name)} />
        </Form.Item>

        {(productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE) && (
          <Form.Item label={t('Número de serie')} name={elementFormName}>
            {editEnabled ? (
              <AntdSelect
                {...serialNumbersSelectProps}
                placeholder={elementFormPlaceholder}
                allowClear
                showSearch
              />
            ) : (
              <TextField value={serializableProductResult?.name ?? '-'} />
            )}
          </Form.Item>
        )}
        <Form.Item label={t('En período de garantía')}>
          <TextField value={in_warranty_period ? 'Sí' : 'No'} />
        </Form.Item>
        {in_warranty_period && (
          <Flex vertical style={{ marginBottom: 24 }}>
            <Form.Item style={{ marginBottom: 0 }} label={<>{t('Fecha de inicio de garantía')}</>}>
              {warranty_start_date ? <DateField value={warranty_start_date} /> : '-'}
            </Form.Item>
            {typeof itemPackage != 'number' && itemPackage?.movil_link && (
              <LinkTextField label={t('Link a móvil')} to={itemPackage.movil_link} external />
            )}
          </Flex>
        )}
        {packageStatus != PACKAGE_STATES.PENDIENTE && (
          <Form.Item
            label={t('Estado recibido')}
            name={canEditResolution ? 'item_received_state' : undefined}>
            {canEditResolution ? (
              <AntdSelect
                options={Object.entries(ReceivedProductStatusStrings).map(([key, value]) => ({
                  label: value,
                  value: parseInt(key),
                }))}
                allowClear
              />
            ) : (
              <TextField
                value={
                  item_received_state != undefined
                    ? ReceivedProductStatusStrings[item_received_state]
                    : '-'
                }
              />
            )}
          </Form.Item>
        )}
      </fieldset>

      {item_shipment_related ? (
        <>
          <fieldset>
            <legend>{t('Información del envío asociado')}</legend>
            <Form.Item label={t('Cantidad enviada')}>
              <NumberField value={requested_amount} />
            </Form.Item>
            <Form.Item
              tooltip={t('Cantidad utilizada en el servicio')}
              label={t('Cantidad utilizada')}
              name={editableRelatedQuantities ? 'used_amount' : undefined}
              rules={[
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error(t('Ingrese un numero valido')));
                    }
                  },
                },
              ]}>
              {editableRelatedQuantities ? (
                <InputNumber style={{ width: '100%' }} min={0} />
              ) : (
                <NumberField value={used_amount} />
              )}
            </Form.Item>
          </fieldset>
          <fieldset>
            <legend>{t('Información de la recepción')}</legend>
            <Form.Item
              tooltip={t('Cantidad que debe ser devuelta a Acronex')}
              label={t('Cantidad a recibir')}
              name={editableRelatedQuantities ? 'to_return_amount' : undefined}
              rules={[
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error(t('Ingrese un numero valido')));
                    }
                  },
                },
              ]}>
              {editableRelatedQuantities ? (
                <InputNumber style={{ width: '100%' }} min={0} />
              ) : (
                <NumberField value={to_return_amount} />
              )}
            </Form.Item>
            <Form.Item
              tooltip={
                hasTechnician
                  ? t('Cantidad que se quedara en el stock del técnico')
                  : t('Debe asignarse un técnico para poder editar esta cantidad')
              }
              label={t('Cantidad para stock del técnico')}
              name={editableRelatedQuantities ? 'technician_stock_amount' : undefined}
              rules={[
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error(t('Ingrese un numero valido')));
                    }
                  },
                },
              ]}>
              {editableRelatedQuantities ? (
                <InputNumber disabled={!hasTechnician} style={{ width: '100%' }} min={0} />
              ) : (
                <NumberField value={technician_stock_amount} />
              )}
            </Form.Item>
            <Form.Item
              tooltip={t('Cantidad que fue descartada')}
              label={t('Cantidad descartada')}
              name={editableRelatedQuantities ? 'discarded_amount' : undefined}
              rules={[
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error(t('Ingrese un numero valido')));
                    }
                  },
                },
              ]}>
              {editableRelatedQuantities ? (
                <InputNumber style={{ width: '100%' }} min={0} />
              ) : (
                <NumberField value={discarded_amount} />
              )}
            </Form.Item>
            {packageStatus == PACKAGE_STATES.PENDIENTE && (
              <Alert
                showIcon
                style={{ fontSize: 12, marginBottom: 12 }}
                type={isValidAmount ? 'success' : 'warning'}
                message={t(
                  'La suma de las cantidades a recibir, descartadas y para stock del técnico debe ser igual a la cantidad enviada para poder marcar como EN VIAJE',
                )}
              />
            )}
          </fieldset>
        </>
      ) : (
        <fieldset>
          <legend>{t('Información de la recepción')}</legend>
          <Form.Item label={t('Cantidad a recibir')}>
            <NumberField value={requested_amount} />
          </Form.Item>
        </fieldset>
      )}

      {packageStatus != PACKAGE_STATES.PENDIENTE && (
        <>
          <Form.Item
            label={t('Cantidad recibida')}
            name={editEnabled ? 'execute_amount' : undefined}
            rules={[
              {
                validator: async (_, value) => {
                  if (value === undefined || value === null) {
                    return Promise.reject(new Error(t('Ingrese un numero valido')));
                  }
                },
              },
            ]}>
            {editEnabled ? (
              <InputNumber style={{ width: '100%' }} min={0} />
            ) : (
              <NumberField value={execute_amount} />
            )}
          </Form.Item>
          <Form.Item label={t('Es una devolución')}>
            <TextField value={is_returned ? 'Sí' : 'No'} />
          </Form.Item>

          <Form.Item label={t('Motivo de devolución')}>
            <TextField
              value={return_type != undefined ? t(ReturnItemTypeString[return_type]) : '-'}
            />
          </Form.Item>
        </>
      )}
    </>
  );
};

export default ReceptionPackageItemShow;
